import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo } from 'react';
import { DialogContext } from '../../../../store/context/DialogContext';
import InputLabel from '../../../../components/InputField/InputLabel';
import { Input, Row } from 'reactstrap';
import { WrapperField } from '../../../../components/InputField/WrapperField';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import {
    collectVisibleFieldErrors,
    doesSelectedBusinessTypeRequirePrime,
    getBusinessTypeRule,
    getFieldErrorValue,
    getNextStepV2,
    getVisibleWizardFieldOrder,
    V2_TYPE,
} from './helperV2';

const BusinessType = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    isForEDD,
    handleChange,
    handlePrev,
    applicationForm,
    editMode,
    setData,
    formType,
    jsonConfig,
    onPrimeRequired,
    isBrokerMode = false,
    pageNum = 1,
}) => {
    const { showError } = useContext(DialogContext);
    // Broker (sponsored) Business Plus applications don't pick a business category:
    // every Prime-requiring category (MSB / fintech / financial institution) is
    // unavailable in this phase. Instead the broker attests the client is NOT a
    // Fintech/MSB; confirming auto-selects the Plus-eligible "Other" type (the real
    // industry / NAICS code is captured later in the form). This both removes the
    // unnecessary categories and restricts fintechs/MSBs via a self-declaration.
    const PLUS_ELIGIBLE_BUSINESS_TYPE = jsonConfig?.businessTypeConfig?.plusEligibleValue || "Other";
    const brokerNotMsbConfirmed = Array.isArray(data?.businesses)
        && data.businesses.includes(PLUS_ELIGIBLE_BUSINESS_TYPE);
    const handleBrokerNotMsbToggle = (checked) => {
        handleErrors("businesses", "");
        handleChange({
            ...data,
            businesses: checked ? [PLUS_ELIGIBLE_BUSINESS_TYPE] : [],
            // businessOther ("If Others, please specify") is required once "Other" is
            // selected but isn't shown to brokers; default it so the data stays valid.
            businessOther: checked ? (data?.businessOther || PLUS_ELIGIBLE_BUSINESS_TYPE) : data?.businessOther,
        });
    };
    const currentApplicationType = Number(applicationForm?.type);
    const isPrimeApplication = currentApplicationType === V2_TYPE.PRIME;
    const isUpgradeApplication = Number(applicationForm?.type) === V2_TYPE.UPGRADE;
    const shouldRenderApplicantIdentity = isUpgradeApplication;
    const visibleFieldOrder = useMemo(() => getVisibleWizardFieldOrder({
        fields: iPlusFields,
        wizard,
        data,
        type: Number(applicationForm?.type),
    }), [applicationForm?.type, data, iPlusFields, wizard]);
    
    const fieldMap = useMemo(() => ({
        ...cyclosFields,
        businesses: {
            ...(cyclosFields?.businesses || {}),
            uiClassName: cyclosFields?.businesses?.uiClassName || "my-1 col-12",
            optionsRowClassName: cyclosFields?.businesses?.optionsRowClassName || "row g-0",
            optionClassName: cyclosFields?.businesses?.optionClassName || "col-12 mb-2",
        },
    }), [cyclosFields]);
    const identityFieldOrder = useMemo(
        () => visibleFieldOrder.filter((field) => ["firstName", "middleName", "lastName", "applicantType"].includes(field)),
        [visibleFieldOrder],
    );
    const businessTypeFieldOrder = useMemo(
        () => visibleFieldOrder.filter((field) => !identityFieldOrder.includes(field)),
        [identityFieldOrder, visibleFieldOrder],
    );
    // In broker mode the business-category selection (`businesses` / `businessOther`) is
    // replaced by the not-a-Fintech/MSB declaration; every OTHER BusinessType field
    // (crypto involvement, source of funds, how-did-you-hear, etc.) must still render.
    const brokerBusinessTypeFieldOrder = useMemo(
        () => businessTypeFieldOrder.filter((field) => !["businesses", "businessOther"].includes(field)),
        [businessTypeFieldOrder],
    );
    // Upgrade applications always collect the authorized person's identity
    // (first/middle/last name + applicant type). These are ordinary editable
    // fields — no per-field hiding based on the connected Plus account.
    const renderedIdentityFieldOrder = useMemo(
        () => (shouldRenderApplicantIdentity ? identityFieldOrder : []),
        [identityFieldOrder, shouldRenderApplicantIdentity],
    );
    const shouldRenderIdentityField = (field) => renderedIdentityFieldOrder.includes(field);
    const hiddenIdentityFieldOrder = useMemo(
        () => identityFieldOrder.filter((field) => !renderedIdentityFieldOrder.includes(field)),
        [identityFieldOrder, renderedIdentityFieldOrder],
    );
    const renderedFieldOrder = useMemo(
        () => [...renderedIdentityFieldOrder, ...businessTypeFieldOrder],
        [businessTypeFieldOrder, renderedIdentityFieldOrder],
    );

    const checkNext = () => {
        if (isBrokerMode && !brokerNotMsbConfirmed) {
            setHasError(true);
            const message = "Please confirm you are not a Fintech or Money Service Business to continue.";
            handleErrors("businesses", message);
            showError(message);
            return false;
        }
        const baseErrors = hiddenIdentityFieldOrder.length
            ? _.omit(errors || {}, hiddenIdentityFieldOrder)
            : (errors || {});
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors: baseErrors,
            type: Number(applicationForm?.type),
        });
        const mergedErrors = {
            ...baseErrors,
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const fieldPath of renderedFieldOrder) {
            const fieldError = getFieldErrorValue(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                if (fieldError) {
                    showError(fieldError);
                    return false;
                }
            }
        }

        setErrors({});
        return true;
    };

    const handleSubmit = async () => {
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            if (
                Number(applicationForm?.type) === V2_TYPE.PLUS
                && doesSelectedBusinessTypeRequirePrime(data, jsonConfig)
            ) {
                const businessTypeRule = getBusinessTypeRule(data, jsonConfig);
                return onPrimeRequired?.({ businessTypeRule });
            }
            return handleUpdate({ nextStep: getNextStepV2(wizard, Number(applicationForm?.type), data, jsonConfig) });
        }
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData);
    };

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => {
            const updatedErrors = _.cloneDeep(prevErrors);
            updatedErrors[field] = errorMsg;
            return updatedErrors;
        });
    };

    return (
        <div className="business-v2-step-shell" data-testid="business-v2-business-type-step">
            <div className='business-v2-step-section'>
                    {isUpgradeApplication ? (
                        <p className='business-v2-step-copy'>
                            This form is for Business Plus Customers to upgrade their account from Business Plus to Business Prime. Business Prime is for accountholders with a need for higher limits or for accounts involving third party funds such as an MSB, Non Bank Financial Services, VASPS, Exchanges and OTC&apos;s.
                        </p>
                    ) : null}
                    {!isUpgradeApplication && !isBrokerMode ? (
                        <p className='business-v2-step-copy'>
                            {isPrimeApplication
                                ? 'Select the option that best describes your business. This selection determines which Business Prime follow-up pages are required for your application.'
                                : 'Select the option that best describes your business. This selection determines whether your application can continue on Business Plus or must proceed on Business Prime.'}
                        </p>
                    ) : null}
                    {shouldRenderApplicantIdentity ? (
                        <div className='business-v2-step-section'>
                            <InputLabel
                                label="Authorized person completing this application"
                                labelStyle={{ fontSize: 15 }}
                            />
                            <Row className='g-3 business-v2-step-inline-row'>
                                {shouldRenderIdentityField("firstName") ? (
                                    <WrapperField
                                        disabled={asLabel}
                                        className="col-md-4"
                                        jsonField={cyclosFields["firstName"]}
                                        dataObj={data}
                                        onChange={handleUpdateFormData}
                                        showError={hasError}
                                        onError={(errorMsg) => handleErrors('firstName', errorMsg)}
                                    />
                                ) : null}
                                {shouldRenderIdentityField("middleName") ? (
                                    <WrapperField
                                        disabled={asLabel}
                                        className="col-md-4"
                                        jsonField={cyclosFields["middleName"]}
                                        dataObj={data}
                                        onChange={handleUpdateFormData}
                                        showError={hasError}
                                        onError={(errorMsg) => handleErrors('middleName', errorMsg)}
                                    />
                                ) : null}
                                {shouldRenderIdentityField("lastName") ? (
                                    <WrapperField
                                        disabled={asLabel}
                                        className="col-md-4"
                                        jsonField={cyclosFields["lastName"]}
                                        dataObj={data}
                                        onChange={handleUpdateFormData}
                                        showError={hasError}
                                        onError={(errorMsg) => handleErrors('lastName', errorMsg)}
                                    />
                                ) : null}
                                {shouldRenderIdentityField("applicantType") ? (
                                    <WrapperField
                                        disabled={asLabel}
                                        jsonField={cyclosFields["applicantType"]}
                                        dataObj={data}
                                        onChange={handleUpdateFormData}
                                        showError={hasError}
                                        onError={(errorMsg) => handleErrors('applicantType', errorMsg)}
                                    />
                                ) : null}
                            </Row>
                        </div>
                    ) : null}
                    {isBrokerMode ? (
                        <>
                            <p className='business-v2-step-copy'>
                                FV Bank managed accounts are available to non-financial businesses. Please confirm the declaration below to continue.
                            </p>
                            <div className='business-v2-certification-card'>
                                <label className={`business-v2-step-terms mb-0 ${asLabel ? "is-disabled" : ""}`}>
                                    <Input
                                        type="checkbox"
                                        checked={brokerNotMsbConfirmed}
                                        disabled={asLabel}
                                        onChange={(event) => handleBrokerNotMsbToggle(event?.target?.checked)}
                                        data-testid="business-v2-not-msb-confirm"
                                    />
                                    <span className='business-v2-step-terms-copy'>
                                        Confirm you are not a Fintech or Money Service Business company !
                                    </span>
                                </label>
                            </div>
                            {hasError && !brokerNotMsbConfirmed ? (
                                <div className="ps-0 ms-0 w-100 error__message left-0">
                                    Please confirm you are not a Fintech or Money Service Business to continue.
                                </div>
                            ) : null}
                            {brokerBusinessTypeFieldOrder.length ? (
                                <BusinessV2FieldGrid
                                    fieldOrder={brokerBusinessTypeFieldOrder}
                                    fieldMap={fieldMap}
                                    data={data}
                                    asLabel={asLabel}
                                    handleChange={handleUpdateFormData}
                                    hasError={hasError}
                                    onFieldError={handleErrors}
                                    dataTestId='business-v2-business-type-fields'
                                />
                            ) : null}
                        </>
                    ) : (
                        <BusinessV2FieldGrid
                            fieldOrder={businessTypeFieldOrder}
                            fieldMap={fieldMap}
                            data={data}
                            asLabel={asLabel}
                            handleChange={handleUpdateFormData}
                            hasError={hasError}
                            onFieldError={handleErrors}
                            dataTestId='business-v2-business-type-fields'
                        />
                    )}
            </div>

            <div className='business-v2-step-actions'>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={() => { handlePrev() }}
                    showBackOnFirstStep={currentApplicationType === V2_TYPE.PLUS}
                    step={pageNum}
                    showPageNum={false}
                    pageNum={pageNum}
                    hideSubmitButton={hideButtons}
                    asLabel={false}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default observer(BusinessType);
